import React, { useState, useEffect } from "react";
import styled, { css } from "styled-components";
import { useTheme } from "@material-ui/styles";
import { useMediaQuery } from "@material-ui/core";
import { Theme } from "../../../theme";
import useInterval from "../../../utils/functions/useInterval";
import LinearProgress from "@material-ui/core/LinearProgress";
import CameraHeader from "./CameraHeader";
import CameraMeasurements from "./CameraMeasurements";
import {
  LineChart,
  ResponsiveContainer,
  CartesianGrid,
  XAxis,
  Line,
  Tooltip,
  YAxis,
} from "recharts";
import { imgText } from "../Views/Cameras/imgText";
import { imgTextHeatmap } from "../Views/Cameras/imgTextHeatmap";
import { imgTextHeatmap2 } from "../Views/Cameras/imgTextHeatmap2";
import CameraImage from "./CameraImage";
import { overallRisk } from "./measurementRisk";
import { useMeasurement } from "./hooks";
// import moment from "moment";

const StyledSection = styled.section`
  display: flex;
  flex-direction: column;
  padding: 8px;
  margin-bottom: 8px;
  border: 1px solid #aaa;
  border-radius: 5px;
  background-color: #fff;

  ${({ theme }: { theme: Theme }) => {
    return css`
      div.deploymentCard {
        display: flex;
        flex-direction: row-reverse;
        height: auto;

        .image {
          position: relative;
          width: 320px;
          height: 180px;
          /* border: 1px solid red; */
          flex-shrink: 0;

          img.cameraFeed {
            position: relative;
            width: 100%;
            height: 100%;
            border-radius: 5px;
            z-index: 1050;
            display: block;
            margin: auto;
          }
          img.heatOverlay {
            z-index: 1090;
            opacity: 0.4;
            border-radius: 5px;
            position: absolute;
            top: 0px;
            left: 0px;
            width: 320px;
            height: 180px;
          }
        }

        .measurementsChart {
          height: 180px;
          flex: 1;
          /* border: 1px solid red; */
          border-radius: 5px;
        }
      }
    `;
  }};
`;

//FOR TESTING ONLY
const TEST_DATA = Array.from({ length: 10 }, (v, k) => ({
  mood: ~~(10 * Math.random()),
  density: ~~(10 * Math.random()),
  flow: ~~(10 * Math.random()),
  timestamp: new Date(new Date().getTime() + 1000 * k).getTime(),
}));
const TEST_IMAGE = `${imgText}`;
const TEST_IMAGE_HEATMAP = `${imgTextHeatmap}`;

export interface CameraCardProps {
  deploymentIds: number[];
  timestamp: Date;
  location: string; //Might grab location from custom data hook instead
  weights: { m: number; d: number; f: number };
  riskWeights: { m: number; d: number; f: number };
  defaults: { m: number; d: number; f: number };
}

//Layout is determined here
const CameraCard: React.FC<CameraCardProps> = props => {
  const theme: Theme = useTheme();
  const mdUp = useMediaQuery(theme.breakpoints.up("md"));

  //TESTING
  const maxMeasurements = {
    maxDensity: 5,
    maxFlow: 40,
    maxMood: 1,
  };

  //States
  const [measurementData, setters] = useMeasurement(
    props.deploymentIds,
    props.timestamp,
    props.weights,
    props.defaults
  );

  //Move this over to measurements
  // const measurements = {
  //   density:
  //     measurementData.slice.density * props.weights.d ||
  //     props.defaults.d + Math.random() * 0.2,
  //   flow:
  //     measurementData.slice.flow * props.weights.f ||
  //     props.defaults.f + Math.random() * 0.2,
  //   mood:
  //     measurementData.slice.mood * props.weights.m ||
  //     props.defaults.m + Math.random() * 0.2,
  // };
  const measurements = {
    density: measurementData.slice.density,
    flow: measurementData.slice.flow,
    mood: measurementData.slice.mood,
  };

  const [location, setLocation] = useState(props.location);
  const [heatmapActive, setHeatmapActive] = useState(false);
  const [risk, setRisk] = useState(
    overallRisk(measurements, props.riskWeights)
  );
  const [timestamp, setTimestamp] = useState(new Date());
  const { data, loading, play, count } = measurementData;
  const { setPlay, setSlice, setTime } = setters;

  //Reset risk matrix value
  useEffect(() => {
    setRisk(overallRisk(measurements, props.riskWeights));
  }, [measurementData.slice]);

  useInterval(() => {
    //DO SOMETHING
  }, 1000);

  const toggleSwitch = () => {
    console.log("toggled");
    setHeatmapActive(!heatmapActive);
  };

  const handleSliderChange = (
    event: React.ChangeEvent<{}>,
    newValue: number | number[]
  ) => {
    let value: number;
    if (typeof newValue === "number") {
      value = newValue;
    } else {
      value = newValue.length > 0 ? newValue[0] : 0;
    }
    const index = ~~((data.length * value) / 100);
    setters.setCount(index);
  };

  return (
    <>
      <StyledSection theme={theme}>
        <CameraHeader
          heatmapActive={heatmapActive}
          toggleSwitch={toggleSwitch}
          location={location}
          riskColor={risk.backgroundColor}
          timestamp={new Date(measurementData.slice.timestamp) || timestamp}
          handleSliderChange={handleSliderChange}
          sliderValue={~~((count / data.length) * 100)}
        ></CameraHeader>
        <div className="deploymentCard">
          <CameraImage
            image={measurementData.slice.image || TEST_IMAGE}
            // image={
            //   props.deploymentIds[0] === 13
            //     ? TEST_IMAGE
            //     : measurementData.slice.image || TEST_IMAGE
            // } //FOR SAMPLE CROWD IMAGE
            heatmap={measurementData.slice.heatmap || TEST_IMAGE_HEATMAP}
            showHeatmap={heatmapActive}
          ></CameraImage>
          <CameraMeasurements
            measurements={measurements}
            maxMeasurements={maxMeasurements}
          ></CameraMeasurements>
          {mdUp && (
            <div className="measurementsChart">
              <ResponsiveContainer width={"100%"} height={"100%"}>
                <LineChart className="chart" data={measurementData.data}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="timestamp"
                    interval="preserveStartEnd"
                    tickFormatter={(value: any) => {
                      try {
                        return new Date(value).toLocaleTimeString();
                      } catch (error) {
                        return value;
                      }
                    }}

                    // tickFormatter={(time: number) =>
                    //   new Date(time).toLocaleTimeString()
                  />
                  <YAxis type="number" interval="preserveStartEnd" />
                  <Tooltip
                    labelFormatter={(value: any) => {
                      try {
                        return `${new Date(
                          value
                        ).toLocaleDateString()} ${new Date(
                          value
                        ).toLocaleTimeString()}`;
                      } catch (error) {
                        return value;
                      }
                    }}
                    formatter={(value: any, name: any) => {
                      // if (name === "flow") {
                      //   return (value * 5).toFixed(2);
                      // }
                      if (typeof value === "number") {
                        return value.toFixed(2);
                      } else {
                        return value;
                      }
                    }}
                    // label={(time: number) =>
                    //   new Date(time).toLocaleTimeString()
                    // }
                  />
                  {/* Needs a formatter for tooltip */}
                  <Line
                    type="monotone"
                    dataKey="mood"
                    stroke="#aa0000"
                    dot={false}
                    connectNulls={true}
                  />
                  <Line
                    type="monotone"
                    dataKey="density"
                    stroke="#00aa00"
                    dot={false}
                    connectNulls={true}
                  />
                  <Line
                    type="monotone"
                    dataKey="flow"
                    stroke="#0000aa"
                    dot={false}
                    connectNulls={true}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </StyledSection>
    </>
  );
};

export default CameraCard;
